// src/pages/PatientsOverview/components/CompanyModal.js
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, DatePicker, message, Button } from "antd";
import PhoneInput from "react-phone-input-2";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedInsurance,
  selectIsEditInsurance,
} from "../PatientsSelectors";
import { createOrUpdateInsurance, fetchInsuranceList } from "../PatientsThunk";
import { closeInsuranceModal } from "../PatientsSlice";
import dayjs from "dayjs";
import { getISOFromCountryName } from "../../../lib/currency";

const CompanyModal = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const selected = useSelector(selectSelectedInsurance);
  const isEdit = useSelector(selectIsEditInsurance);

  const [phoneInputValue, setPhoneInputValue] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState("");

  const authInfo = useSelector((state) => state.auth.auth_info);

  useEffect(() => {
    if (isEdit && selected) {
      // map selected fields to form fields
      const phone = selected.phone_number || "";
      const countryCode = selected.country_code
        ? String(selected.country_code)
        : "";

      // phoneInputValue expected by react-phone-input-2 is full number WITHOUT the leading "+"
      const combined = countryCode ? `${countryCode}${phone}` : phone;

      form.setFieldsValue({
        company_name: selected.company_name || "",
        valid_upto: selected.valid_upto ? dayjs(selected.valid_upto) : null,
        discount: selected.discount_percentage || selected.discount || "",
        email: selected.email_id || "",
        contact_name: selected.contact_name || "",
        // phone: selected.phone_number || "",
        // country_code: selected.country_code || "",
        phone: phone || "",
        country_code: countryCode || "",
        receiver_id: selected.receiver_id || "",
        payer_id: selected.payer_id || "",
      });
      setPhoneDialCode(countryCode);
      setPhoneInputValue(combined || "");
    } else {
      form.resetFields();
      setPhoneDialCode("");
      setPhoneInputValue("");
    }
  }, [selected, isEdit, form]);

  const handleCancel = () => {
    dispatch(closeInsuranceModal());
    form.resetFields();
    setPhoneDialCode("");
    setPhoneInputValue("");
  };

  const handleFinish = async (values) => {
    // assemble payload to match your API
    const payload = {
      company_name: values.company_name,
      created_date: values.valid_upto ? values.valid_upto.toISOString() : "",
      discount: values.discount,
      email: values.email,
      contact_name: values.contact_name,
      // phone_no: values.phone?.replace(/^\+/, "") || values.phone,
      // country_code: values.country_code || "",
      phone_no: values.phone ? String(values.phone).replace(/\D/g, "") : "",
      country_code: values.country_code || "",
      receiver_id: values.receiver_id,
      payer_id: values.payer_id,
      insurance_action: isEdit ? "update" : "create",
      insurance_edit_id: isEdit ? selected.insurance_company_id : undefined,
    };
 
    // return;
    try {
      await dispatch(createOrUpdateInsurance(payload)).unwrap();
      message.success("Company saved successfully");
      dispatch(closeInsuranceModal());
      dispatch(fetchInsuranceList());
    } catch (err) {
      message.error(err?.message || "Save failed");
    }
  };

  // discount validator: number between 0 and 100 inclusive, up to 2 decimals
  const validateDiscount = (_, value) => {
    if (value === undefined || value === null || value === "") {
      return Promise.reject(new Error("Discount is required"));
    }
    const asStr = String(value).trim();
    // allow integers or decimals with up to 2 decimal places
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(asStr)) {
      return Promise.reject(
        new Error("Enter a valid percentage (up to 2 decimals)")
      );
    }
    const num = Number(asStr);
    if (Number.isNaN(num) || num < 0 || num > 100) {
      return Promise.reject(new Error("Percentage must be between 0 and 100"));
    }
    return Promise.resolve();
  };

  // ✅ Phone validator (7–15 digits)
  const validatePhone = (_, value) => {
    const phone = value ? String(value).replace(/\D/g, "") : "";
    if (!phone) {
      return Promise.reject(new Error("Contact number is required"));
    }
    if (phone.length < 7 || phone.length > 15) {
      return Promise.reject(
        new Error("Enter a valid contact number (7–15 digits)")
      );
    }
    return Promise.resolve();
  };

  // PhoneInput's onChange signature: (value, country, e, formattedValue)
  const onPhoneChange = (value, country) => {
    // value may include the dial code as a prefix. Remove any non-digits.
    const digits = String(value || "").replace(/\D/g, "");
    const dial = country?.dialCode ? String(country.dialCode) : "";

    // Remove the dial from the digits to store the local phone part only
    let local = digits;
    if (dial && local.startsWith(dial)) {
      local = local.slice(dial.length);
    }

    // update form fields (phone without dial and country_code)
    form.setFieldsValue({
      phone: local,
      country_code: dial,
    });

    setPhoneInputValue(digits); // full digits for display
    setPhoneDialCode(dial);
  };

  const defaultCountry = getISOFromCountryName(authInfo?.country);

  return (
    <Modal
      title={isEdit ? "Update Insurance Company" : "New Insurance Company"}
      visible={true}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          name="company_name"
          label="Company Name"
          rules={[{ required: true, message: "Company Name is required" }]}
          data-testid="company_name"
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="valid_upto"
          label="Valid Upto"
          // data-testid="company_valid_upto"
          rules={[{ required: true, message: "Valid Upto is required" }]}
        >
          <DatePicker style={{ width: "100%" }}   data-testid="company_valid_upto"/>
        </Form.Item>
        <Form.Item
          name="discount"
          label="Discount %"
          data-testid="company_discount"
          rules={[{ validator: validateDiscount }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email Id"
          data-testid="company_email_id"
          rules={[
            { required: true, type: "email", message: "Enter valid email" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="contact_name"
          label="Contact Name"
          data-testid="company_contact_name"
          rules={[{ required: true, message: "Contact Name is required" }]}
        >
          <Input />
        </Form.Item>
        {/* <Form.Item label="Phone No">
          <Form.Item name="phone" noStyle>
            <Input />
          </Form.Item>
          {/* If you want react-phone-input-2: wrap or use custom */}
        {/* </Form.Item>



        <Form.Item name="country_code" label="Country Code">
          <Input />
        </Form.Item> */}{" "}
        {/* <Form.Item
          label="Contact No"
          name="phone"
          rules={[
            { required: true, message: "Contact number is required" },
            { pattern: /^[0-9]{7,15}$/, message: "Enter valid contact number" },
          ]}
        >
          <PhoneInput
            country={"us"}
            inputStyle={{
              width: "100%",
              height: "39.6px",
              background: "rgba(0,0,0,0.04)",
              borderColor: "transparent",
            }}
            placeholder="Contact no"
          />
        </Form.Item> */}
        <Form.Item label="Contact No" required>
          <PhoneInput
            country={defaultCountry}
            value={phoneInputValue}
            onChange={onPhoneChange}
            data-testid="company_contact_no"
            inputStyle={{
              width: "100%",
              height: "39.6px",
              background: "rgba(0,0,0,0.04)",
              borderColor: "transparent",
            }}
          // enable area codes and other props if you want
          />
          {/* Hidden fields to hold normalized values inside the antd form */}
          <Form.Item
            name="phone"
            noStyle
            rules={[{ validator: validatePhone }]}
          >
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="country_code" noStyle>
            <Input type="hidden" />
          </Form.Item>
          {/* validation for phone is created below by using getFieldValue on submit client-side or you can add rules to the hidden field */}
        </Form.Item>
        <Form.Item
          name="receiver_id"
          label="Receiver ID"
          data-testid="company_received_id"
          rules={[{ required: true, message: "Discount is required" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="payer_id"
          label="Payer ID"
          data-testid="company_payer_id"
          rules={[{ required: true, message: "Discount is required" }]}
        >
          <Input />
        </Form.Item>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            marginTop: "1.5rem",
          }}
        >
          <Button type="button" onClick={handleCancel}>
            Discard
          </Button>
          <Button htmlType="submit" data-testid="company_save" type="primary">
            {isEdit ? "Update" : "Add Company"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CompanyModal;
